import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import * as React from 'react';
import { OfflineProcessingStageType } from '../../../store/Models/OfflineProcesses/OfflineProcessModels';
import { ProgressBar } from '../../core/ProgressBar';
import { AppServices } from '../../../AppServices';
import { OfflineProcessPollingManager } from '../../../store/Managers/OfflineProcessPollingManager';

export interface OfflineProcessProgressProps {
    offlineProcessId: string;
    appServices: AppServices;
    notCompleteContent?: JSX.Element | null;
    completeContent?: JSX.Element | null;
    onComplete?: () => Promise<void> | null;
}

export interface SurveyOfflineProgressModalProps extends OfflineProcessProgressProps {
    showDataSaveMessage: boolean;
}

export interface OfflineProcessProgressState {
    currentProgressIndex: number;
    totalItems: number;
    intervalId: NodeJS.Timeout | null;
    quotaExceeded: boolean;
    currentProcessingStage: OfflineProcessingStageType;
}

export class OfflineProcessProgress<TProps extends OfflineProcessProgressProps = OfflineProcessProgressProps> extends React.PureComponent<TProps, OfflineProcessProgressState> {
    public constructor(props: TProps) {
        super(props);

        this.state = {
            currentProgressIndex: 0,
            totalItems: 0,
            intervalId: null,
            quotaExceeded: false,
            currentProcessingStage: OfflineProcessingStageType.Queued
        };
    }

    // ===============================================================================

    protected overrideLocalizedMessage: string | null = null;

    // ===============================================================================

    public componentDidMount() {
        this.initiateOfflineProcessStatusPollingLoop();
    }

    // ===============================================================================

    public componentWillUnmount() {
        this.stopPolling();
    }

    // ===============================================================================

    private initiateOfflineProcessStatusPollingLoop() {
        this.initiateGetLatestOfflineStatus();

        const intervalId = setInterval(
            this.initiateGetLatestOfflineStatus,
            1500);

        this.setState({
            intervalId: intervalId
        });
    }

    // ===============================================================================

    protected stopPolling() {
        if (this.state.intervalId !== null) {
            clearInterval(this.state.intervalId);
            this.setState({ intervalId: null });
        }
    }

    // ===============================================================================

    protected isComplete = () => (this.state.totalItems > 0) && (this.state.currentProgressIndex === this.state.totalItems);

    // ===============================================================================

    private initiateGetLatestOfflineStatus = () => {
        const serviceClient = new ToxnotServiceClient();
        serviceClient.offlineProcessGet(this.props.offlineProcessId, false)
            .then(apiResult => {
                if (!apiResult.success || apiResult.data === null) {
                    this.stopPolling();
                    return;
                }

                if ((apiResult.data.state.processingStage === OfflineProcessingStageType.TotallyComplete) ||
                    (apiResult.data.state.processingStage === OfflineProcessingStageType.Failed)) {
                    this.stopPolling();
                }

                this.setState({
                    currentProgressIndex: apiResult.data.state.currentProgressItemIndex,
                    totalItems: apiResult.data.state.progressTotalNumItems,
                    currentProcessingStage: apiResult.data.state.processingStage
                });

                if (apiResult.data.results?.quotaWasExceeded === true) {
                    this.setState({
                        quotaExceeded: true
                    });

                    this.stopPolling();
                }

                if (this.isComplete() && this.props.onComplete) {
                    this.props.onComplete();
                }
            });
    }

    // ===============================================================================

    protected renderStatusContainer(id: string | null = null) {
        const isComplete = this.isComplete();


        const localizedMessageToShow = this.overrideLocalizedMessage != null ? this.overrideLocalizedMessage: OfflineProcessPollingManager.getFriendlyNameForOfflineProcessStage(this.state.currentProcessingStage, this.props.appServices);

        return (
            <div id={id ?? ""}>
                {(isComplete && this.props.completeContent) ? this.props.completeContent : null}
                {(!isComplete && this.props.notCompleteContent) ? this.props.notCompleteContent : null}
                <ProgressBar localizedTitle={localizedMessageToShow} currentProgressIndex={this.state.currentProgressIndex} totalNumItems={this.state.totalItems} localizer={this.props.appServices} />
            </div>
        );
    }

    // ===============================================================================

    public render() {
        return this.renderStatusContainer();
    }
}

// ===============================================================================

// Use this inside private renderModals = () => { which is in CreateAndEditSurveys.tsx
//                <div>
//                    {this.props.surveyUi.activeSendOfflineProcessId !== null && <SurveyOfflineProgressModal offlineProcessId={this.props.surveyUi.activeSendOfflineProcessId} >}
//                </div>

export class SurveyOfflineProgressModal extends OfflineProcessProgress<SurveyOfflineProgressModalProps> {
    public constructor(props: SurveyOfflineProgressModalProps) {
        super(props);
    }

    // ===============================================================================

    protected overrideLocalizedMessage: string | null = this.props.appServices.localizeString("Sending Surveys...");

    // ===============================================================================

    public render() {
        return (
            <div className="modal-content">
                <div className="modal-header">
                    <button type="button" id="offline-progress-x-close-button" className="close" aria-label="Close" onClick={e => this.doneButton_click()}><span aria-hidden="true">&times;</span></button>
                    <h4 className="modal-title">{this.props.showDataSaveMessage ? this.props.appServices.localizeString("3E Exchange is saving your survey") : this.props.appServices.localizeString("3E Exchange is sending your survey")}</h4>
                </div>
                <div className="modal-body">
                    <div className="alert alert-info">
                        <p className="text-center m-b-10-imp">
                            {this.props.appServices.localizeString("Depending on the size of the survey, this might take a couple minutes.")}
                        </p>
                        <p className="text-center">
                            {this.props.appServices.localizeString("You can safely leave this page and check back later by clicking the ‘My Surveys’ tab.")}
                        </p>
                    </div>
                    {this.renderStatusContainer("offline-survey-status-container")}
                </div>
                <div className="modal-footer">
                    <button type="button" id="offline-progress-close-button" className="btn btn-toxnot-primary" onClick={e => this.doneButton_click()}>{this.props.appServices.localizeString("Done")}</button>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private doneButton_click = () => {
        this.stopPolling();
        window.location.href = "/Surveys/SupplierSurveys";
    }
}